import React, { createContext, useCallback, useContext, useMemo, useReducer } from "react";
import { api } from "@/lib/api";
// Relative on purpose: no "@" alias on the Jest side (cf. LanguageAwareField.jsx).
import usePolling from "../hooks/usePolling";
import { payloadShapeError } from "../hooks/useAdminLoad";

/**
 * ONE POLL OF THE INBOX FOR THE WHOLE CONSOLE.
 *
 * Three surfaces need the same fact — the sidebar's unread pill (rendered TWICE, desktop
 * and mobile drawer, from the same AdminSidebar), and the Messages screen's list. Giving
 * each its own `GET /admin/conversations` would poll the same endpoint three times a
 * minute and let the three disagree, which on a badge is worse than being slow: a pill
 * saying "2" beside a list showing none is a screen arguing with itself. The provider is
 * mounted once, in AdminLayout, so all three read one state.
 *
 * THE CURSOR IS THE SERVER'S, ECHOED BACK. `since` is whatever `cursor` the last response
 * carried — never a browser clock, which would drift against the database's own
 * timestamps and silently drop a conversation written during the skew. The server's
 * window is INCLUSIVE at the cursor (repository.list_since), so the boundary row comes
 * back every time and the merge is by `id`: a repeat replaces a row, it never duplicates
 * one.
 *
 * `unreadCount` IS THE SERVER'S NUMBER, corrected locally for the one transition the
 * reader has just caused. The badge cannot wait up to 60 s to stop claiming an unread
 * thread the practitioner is reading right now, so `patchConversation` — fed the
 * ConversationOut that `PUT …/read` returns — applies that row's own unread→read
 * transition to the count. It is the same arithmetic the next poll will report; the poll
 * remains the authority and overwrites it.
 *
 * A FAILED POLL IS NOT AN EMPTY INBOX (BUG-051): the error is kept as `loadError`, the
 * rows already fetched are kept as they are, and the reader decides what to render. The
 * inbox renders the failure and withholds "no conversations yet".
 */
const ConversationsContext = createContext(null);

export const POLL_INTERVAL_MS = 60000;
const ENDPOINT = "/admin/conversations";

/** An error with no message, so the SCREEN's localized sentence is what gets read (the
 *  reasoning is `useAdminLoad`'s: a message invented here would be English in every
 *  locale). Used only when a promise rejects with no reason at all. */
const readFailed = () => Object.assign(new Error(), { name: "ReadFailed" });

const timeOf = (c) => {
  const t = Date.parse(c?.last_message_at || "");
  return Number.isNaN(t) ? 0 : t;
};

/**
 * Merge by `id`, newest `last_message_at` first. Exported for the test that pins it: the
 * inclusive cursor means the same conversation arrives again on the very next poll, and a
 * list that appended instead of replacing would grow a duplicate row per minute.
 */
export const mergeConversations = (prev, incoming) => {
  const byId = new Map(prev.map((c) => [c.id, c]));
  for (const c of incoming) {
    if (c && c.id) byId.set(c.id, c);
  }
  return [...byId.values()].sort((a, b) => timeOf(b) - timeOf(a));
};

const INITIAL = { conversations: [], unreadCount: 0, loadError: null, cursor: null };

function reducer(state, action) {
  switch (action.type) {
    case "loaded": {
      const { conversations, unread_conversations: unread, cursor } = action.payload;
      return {
        conversations: mergeConversations(state.conversations, conversations),
        // The badge only moves on a number the server actually sent: a body missing the
        // field must not silently zero a pill that is telling the truth.
        unreadCount: Number.isFinite(unread) ? unread : state.unreadCount,
        loadError: null,
        // A response with no rows carries the cursor it was given back; keeping the old
        // one when it is null is the same statement, and survives a null.
        cursor: cursor || state.cursor,
      };
    }
    case "failed":
      return { ...state, loadError: action.error || readFailed() };
    case "patched": {
      const { conversation } = action;
      const before = state.conversations.find((c) => c.id === conversation.id);
      // +1 when this row stopped being unread, -1 when it became unread, 0 otherwise.
      const delta = (before?.practitioner_has_unread ? 1 : 0)
        - (conversation.practitioner_has_unread ? 1 : 0);
      return {
        ...state,
        conversations: mergeConversations(state.conversations, [conversation]),
        unreadCount: Math.max(0, state.unreadCount - delta),
      };
    }
    default:
      return state;
  }
}

export function ConversationsProvider({ children, intervalMs = POLL_INTERVAL_MS, enabled = true }) {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const { cursor } = state;

  // Redefined whenever the cursor moves, which is harmless: `usePolling` reads `fn`
  // through a ref, so the timer is never rescheduled by it.
  const poll = useCallback(async () => {
    try {
      const { data } = await api.get(ENDPOINT, cursor ? { params: { since: cursor } } : undefined);
      // A malformed 200 (the production proxy answers index.html for an unknown /api
      // path) is a FAILURE, not an inbox with nothing in it.
      if (!data || !Array.isArray(data.conversations)) throw payloadShapeError();
      dispatch({ type: "loaded", payload: data });
    } catch (e) {
      dispatch({ type: "failed", error: e });
    }
  }, [cursor]);

  // crm-module BEGIN C3.02 — `enabled` (owner: true, a CRM helper: false, wired by
  // AdminLayout from the signed-in `tenant_role`). The inbox (`GET
  // /api/admin/conversations`) is `server.require_admin` — owner-only, decision 5's
  // role matrix withholds the whole Messages surface from a helper — so a helper's
  // session can never succeed here: polling it anyway would be a 403 every interval,
  // forever, for the life of the console tab, which is exactly what a helper session
  // used to do before this session (AdminLayout mounts this provider once for the
  // whole console, whatever page is open). `refresh()` (an explicit, on-demand call)
  // stays live regardless — nothing in the product calls it for a helper today, but
  // "no scheduled poll" and "no read is possible at all" are different claims and only
  // the first one is true here.
  const { refresh } = usePolling(poll, { intervalMs, enabled });
  // crm-module END

  const patchConversation = useCallback((conversation) => {
    if (conversation && conversation.id) dispatch({ type: "patched", conversation });
  }, []);

  const value = useMemo(
    () => ({
      conversations: state.conversations,
      unreadCount: state.unreadCount,
      loadError: state.loadError,
      refresh,
      patchConversation,
    }),
    [state.conversations, state.unreadCount, state.loadError, refresh, patchConversation],
  );

  return <ConversationsContext.Provider value={value}>{children}</ConversationsContext.Provider>;
}

/**
 * The inbox state. Throws outside the provider on purpose: a silent default would let a
 * badge render "0" and a list render "no conversations" from a tree that is not polling
 * at all — the exact false statement this module exists to prevent.
 */
export function useConversations() {
  const ctx = useContext(ConversationsContext);
  if (!ctx) throw new Error("useConversations must be used inside a ConversationsProvider");
  return ctx;
}
